import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { PageHeader, EmptyState, Chip, LoadingState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Broadcast, Plus, Copy, ArrowClockwise, Trash, SignIn, SignOut, Flask } from "@phosphor-icons/react";
import { toast } from "sonner";

const DIRECTION_LABEL = { in: "IN reader", out: "OUT reader" };

export default function RfidReaders() {
    const [readers, setReaders] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", direction: "in", location: "" });
    const [saving, setSaving] = useState(false);
    const [issuedKey, setIssuedKey] = useState(null); // { id, name, api_key } — shown once
    const [simReaderId, setSimReaderId] = useState("");
    const [simTag, setSimTag] = useState("");
    const [simResult, setSimResult] = useState(null);
    const [simulating, setSimulating] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const [r1, r2] = await Promise.all([api.get("/rfid/readers"), api.get("/rfid/scan-logs")]);
            setReaders(r1.data);
            setLogs(r2.data);
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, []);

    async function submit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            const { data } = await api.post("/rfid/readers", form);
            setIssuedKey({ id: data.id, name: data.name, api_key: data.api_key });
            setOpen(false);
            setForm({ name: "", direction: "in", location: "" });
            load();
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setSaving(false);
        }
    }

    async function toggleActive(reader) {
        try {
            await api.patch(`/rfid/readers/${reader.id}`, { is_active: !reader.is_active });
            load();
        } catch (e) { toast.error(formatError(e)); }
    }

    async function regenerate(reader) {
        if (!confirm(`Regenerate the device key for "${reader.name}"? The old key will stop working immediately.`)) return;
        try {
            const { data } = await api.post(`/rfid/readers/${reader.id}/regenerate-key`);
            setIssuedKey({ id: reader.id, name: reader.name, api_key: data.api_key });
        } catch (e) { toast.error(formatError(e)); }
    }

    async function remove(reader) {
        if (!confirm(`Delete reader "${reader.name}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/rfid/readers/${reader.id}`);
            toast.success("Reader deleted");
            load();
        } catch (e) { toast.error(formatError(e)); }
    }

    function copyKey(key) {
        navigator.clipboard.writeText(key);
        toast.success("Key copied to clipboard");
    }

    async function runSimulation() {
        if (!simReaderId || !simTag.trim()) {
            toast.error("Pick a reader and enter a tag ID");
            return;
        }
        setSimulating(true);
        setSimResult(null);
        try {
            const { data } = await api.post(`/rfid/readers/${simReaderId}/simulate-scan`, { tag_id: simTag.trim() });
            setSimResult(data);
            load();
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setSimulating(false);
        }
    }

    if (loading) return <LoadingState />;

    return (
        <div className="animate-fade-up" data-testid="rfid-readers-page">
            <PageHeader
                overline="Gate · Boom Barrier"
                title="RFID Readers"
                description="Issue a device key for each IN/OUT RFID reader at your gate, assign tag IDs to approved vehicles, and test access logic before wiring real hardware."
                actions={
                    <Button onClick={() => setOpen(true)} className="rounded-full" data-testid="add-rfid-reader-btn">
                        <Plus size={16} className="mr-2" /> Add Reader
                    </Button>
                }
            />

            {readers.length === 0 ? (
                <EmptyState
                    icon={<Broadcast size={48} />}
                    title="No RFID readers yet"
                    description="Add your first IN or OUT reader to get a device key for the boom-barrier controller."
                    action={<Button onClick={() => setOpen(true)} className="rounded-full"><Plus size={16} className="mr-2" />Add Reader</Button>}
                />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
                    {readers.map((r) => (
                        <div key={r.id} data-testid={`rfid-reader-card-${r.id}`} className="bg-white border border-brand-line rounded-sm p-6">
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className={`w-11 h-11 rounded-full flex items-center justify-center ${r.direction === "in" ? "bg-green-100" : "bg-orange-100"}`}>
                                        {r.direction === "in" ? <SignIn size={20} className="text-green-600" /> : <SignOut size={20} className="text-orange-600" />}
                                    </div>
                                    <div>
                                        <p className="font-heading text-lg font-bold text-brand-ink">{r.name}</p>
                                        <p className="text-xs text-brand-inkSoft uppercase tracking-wide">{DIRECTION_LABEL[r.direction]}</p>
                                    </div>
                                </div>
                                <Chip variant={r.is_active ? "success" : "neutral"} data-testid={`rfid-reader-status-${r.id}`}>{r.is_active ? "Active" : "Inactive"}</Chip>
                            </div>
                            {r.location && <p className="text-sm text-brand-inkSoft mb-2">Location: <span className="text-brand-ink font-medium">{r.location}</span></p>}
                            <p className="text-xs text-brand-inkSoft mb-4">Last scan: {r.last_seen_at ? new Date(r.last_seen_at).toLocaleString() : "Never"}</p>
                            <div className="flex flex-wrap gap-2">
                                <Button size="sm" variant="outline" className="rounded-full" onClick={() => toggleActive(r)} data-testid={`rfid-reader-toggle-${r.id}`}>
                                    {r.is_active ? "Deactivate" : "Activate"}
                                </Button>
                                <Button size="sm" variant="outline" className="rounded-full" onClick={() => regenerate(r)} data-testid={`rfid-reader-regenerate-${r.id}`}>
                                    <ArrowClockwise size={14} className="mr-1.5" /> Regenerate Key
                                </Button>
                                <Button size="sm" variant="outline" className="rounded-full border-red-300 text-red-600 hover:bg-red-50" onClick={() => remove(r)} data-testid={`rfid-reader-delete-${r.id}`}>
                                    <Trash size={14} />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Simulate RFID Scan */}
            <div className="bg-white border border-brand-line rounded-sm p-6 mb-10" data-testid="rfid-simulate-section">
                <div className="flex items-center gap-2 mb-1">
                    <Flask size={18} className="text-brand-action" />
                    <h2 className="font-heading text-lg font-bold text-brand-ink">Simulate RFID Scan</h2>
                </div>
                <p className="text-sm text-brand-inkSoft mb-4">Test the access-grant logic for any tag ID against a reader, before any physical hardware is wired up.</p>
                <div className="flex flex-col sm:flex-row gap-3">
                    <Select value={simReaderId} onValueChange={setSimReaderId}>
                        <SelectTrigger className="sm:w-64" data-testid="rfid-sim-reader-select">
                            <SelectValue placeholder="Select a reader" />
                        </SelectTrigger>
                        <SelectContent>
                            {readers.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} ({DIRECTION_LABEL[r.direction]})</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Input
                        placeholder="Tag ID (e.g. E2004001...)"
                        value={simTag}
                        data-testid="rfid-sim-tag-input"
                        onChange={(e) => setSimTag(e.target.value)}
                        className="sm:flex-1"
                    />
                    <Button onClick={runSimulation} disabled={simulating} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white" data-testid="rfid-sim-run-btn">
                        {simulating ? "Scanning…" : "Simulate Scan"}
                    </Button>
                </div>
                {simResult && (
                    <div className={`mt-4 p-4 rounded-sm border ${simResult.access === "granted" ? "bg-[#ECFDF5] border-[#A7F3D0]" : "bg-[#FEF2F2] border-[#FECACA]"}`} data-testid="rfid-sim-result">
                        <p className={`font-heading font-bold text-lg ${simResult.access === "granted" ? "text-green-700" : "text-red-700"}`}>
                            {simResult.access === "granted" ? "ACCESS GRANTED — Barrier opens" : "ACCESS DENIED"}
                        </p>
                        {simResult.reason && <p className="text-sm text-brand-inkSoft mt-1">Reason: {simResult.reason.replaceAll("_", " ")}</p>}
                        {simResult.vehicle_number && (
                            <p className="text-sm text-brand-ink mt-1">{simResult.vehicle_number} · {simResult.owner_name} · Flat {simResult.flat_label}</p>
                        )}
                    </div>
                )}
            </div>

            {/* Recent scan logs */}
            <div className="bg-white border border-brand-line rounded-sm overflow-hidden" data-testid="rfid-scan-logs-section">
                <div className="p-6 border-b border-brand-line">
                    <h2 className="font-heading text-lg font-bold text-brand-ink">Recent RFID Scans</h2>
                </div>
                {logs.length === 0 ? (
                    <p className="p-6 text-sm text-brand-inkSoft">No scans recorded yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-brand-bg text-xs uppercase tracking-wide text-brand-inkSoft">
                                <tr>
                                    <th className="p-3">Time</th>
                                    <th className="p-3">Reader</th>
                                    <th className="p-3">Tag ID</th>
                                    <th className="p-3">Vehicle</th>
                                    <th className="p-3">Result</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.slice(0, 100).map((l) => (
                                    <tr key={l.id} className="border-t border-brand-line" data-testid={`rfid-scan-log-row-${l.id}`}>
                                        <td className="p-3 text-sm text-brand-ink">{new Date(l.scanned_at).toLocaleString()}</td>
                                        <td className="p-3 text-sm text-brand-ink">{l.reader_name} ({DIRECTION_LABEL[l.direction]})</td>
                                        <td className="p-3 text-sm font-mono text-brand-ink">{l.tag_id}</td>
                                        <td className="p-3 text-sm text-brand-ink">{l.vehicle_number ? `${l.vehicle_number} · ${l.flat_label || "—"}` : "—"}</td>
                                        <td className="p-3">
                                            <Chip variant={l.result === "granted" ? "success" : "danger"}>{l.result}</Chip>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Add Reader Dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Add RFID Reader</DialogTitle></DialogHeader>
                    <form onSubmit={submit} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Reader Name *</Label>
                            <Input required placeholder="Main Gate IN" value={form.name} data-testid="rfid-reader-name-input"
                                onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                            <Label>Direction *</Label>
                            <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                                <SelectTrigger data-testid="rfid-reader-direction-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="in">IN (entry)</SelectItem>
                                    <SelectItem value="out">OUT (exit)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Location (optional)</Label>
                            <Input placeholder="Main gate, Block A barrier..." value={form.location}
                                onChange={(e) => setForm({ ...form, location: e.target.value })} />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                            <Button type="submit" disabled={saving} data-testid="rfid-reader-submit-btn" className="bg-brand-action hover:bg-brand-actionHover text-white">
                                {saving ? "Creating…" : "Create Reader"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Issued key reveal — shown ONCE */}
            <Dialog open={!!issuedKey} onOpenChange={() => setIssuedKey(null)}>
                <DialogContent data-testid="rfid-issued-key-dialog">
                    <DialogHeader><DialogTitle>Device key for "{issuedKey?.name}"</DialogTitle></DialogHeader>
                    <p className="text-sm text-brand-inkSoft">
                        Copy this key into your reader/boom-barrier controller's configuration now — for security,
                        it will not be shown again. If you lose it, come back here and regenerate a new one.
                    </p>
                    <div className="flex items-center gap-2 p-3 bg-brand-bg border border-brand-line rounded-sm">
                        <code className="flex-1 text-xs break-all" data-testid="rfid-issued-key-value">{issuedKey?.api_key}</code>
                        <Button size="sm" variant="outline" onClick={() => copyKey(issuedKey?.api_key)} data-testid="rfid-issued-key-copy-btn">
                            <Copy size={14} />
                        </Button>
                    </div>
                    <div className="text-xs text-brand-inkSoft bg-brand-sage/20 p-3 rounded-sm space-y-1">
                        <p className="font-semibold text-brand-ink">Integration contract for your hardware integrator:</p>
                        <p>POST this backend's <code>/api/rfid/scan</code> with JSON body <code>{'{"reader_key": "<this key>", "tag_id": "<scanned tag>"}'}</code>.</p>
                        <p>Response is always 200 with <code>{'{"access": "granted"|"denied", ...}'}</code> — open the barrier only when <code>access === "granted"</code>.</p>
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setIssuedKey(null)} className="rounded-full">Done</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
